import apiClient from '../../../apiClient';

/**
 * Recruiting Analytics API Service
 * Handles all API calls for the recruiting analytics dashboard
 */

const recruitingService = {
    /**
     * Get executive dashboard summary (all KPIs)
     * @param {Object} filters - { startDate, endDate, department, jobTitle }
     */
    async getExecutiveSummary(filters = {}) {
        const params = new URLSearchParams();
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        if (filters.department) params.append('department', filters.department);
        if (filters.jobTitle) params.append('jobTitle', filters.jobTitle);

        const queryString = params.toString();
        const url = `/recruiting/executive/summary${queryString ? `?${queryString}` : ''}`;
        const response = await apiClient.get(url);
        return response.data;
    },

    /**
     * Get hiring velocity trend data
     * @param {Object} filters - { months }
     */
    async getHiringVelocity(filters = {}) {
        const params = new URLSearchParams();
        if (filters.months) params.append('months', filters.months);

        const queryString = params.toString();
        const url = `/recruiting/executive/hiring-velocity${queryString ? `?${queryString}` : ''}`;
        const response = await apiClient.get(url);
        return response.data;
    },

    /**
     * Get funnel efficiency data
     * @param {Object} filters - { startDate, endDate, department, jobTitle }
     */
    async getFunnelEfficiency(filters = {}) {
        const params = new URLSearchParams();
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        if (filters.department) params.append('department', filters.department);
        if (filters.jobTitle) params.append('jobTitle', filters.jobTitle);

        const queryString = params.toString();
        const url = `/recruiting/executive/funnel${queryString ? `?${queryString}` : ''}`;
        const response = await apiClient.get(url);
        return response.data;
    },

    /**
     * Get source effectiveness data
     * @param {Object} filters - { months }
     */
    async getSourceEffectiveness(filters = {}) {
        const params = new URLSearchParams();
        if (filters.months) params.append('months', filters.months);

        const queryString = params.toString();
        const url = `/recruiting/executive/sources${queryString ? `?${queryString}` : ''}`;
        const response = await apiClient.get(url);
        return response.data;
    },

    /**
     * Get rejection analysis data
     * @param {Object} filters - { startDate, endDate }
     */
    async getRejectionAnalysis(filters = {}) {
        const params = new URLSearchParams();
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);

        const queryString = params.toString();
        const url = `/recruiting/executive/rejections${queryString ? `?${queryString}` : ''}`;
        const response = await apiClient.get(url);
        return response.data;
    },

    /**
     * Get jobs at risk data
     * @param {Object} filters - { limit }
     */
    async getJobsAtRisk(filters = {}) {
        const params = new URLSearchParams();
        if (filters.limit) params.append('limit', filters.limit);

        const queryString = params.toString();
        const url = `/recruiting/executive/jobs-at-risk${queryString ? `?${queryString}` : ''}`;
        const response = await apiClient.get(url);
        return response.data;
    },

    /**
     * Get pipeline summary
     * @param {Object} filters - { recruiter, department, jobStatus }
     */
    async getPipelineSummary(filters = {}) {
        const params = new URLSearchParams();
        if (filters.recruiter) params.append('recruiter', filters.recruiter);
        if (filters.department) params.append('department', filters.department);
        if (filters.jobStatus) params.append('jobStatus', filters.jobStatus);

        const queryString = params.toString();
        const url = `/recruiting/pipeline/summary${queryString ? `?${queryString}` : ''}`;
        const response = await apiClient.get(url);
        return response.data;
    },

    /**
     * Get pipeline breakdown by stage
     * @param {Object} filters - { recruiter, department, jobStatus }
     */
    async getPipelineByStage(filters = {}) {
        const params = new URLSearchParams();
        if (filters.recruiter) params.append('recruiter', filters.recruiter);
        if (filters.department) params.append('department', filters.department);
        if (filters.jobStatus) params.append('jobStatus', filters.jobStatus);

        const queryString = params.toString();
        const url = `/recruiting/pipeline/by-stage${queryString ? `?${queryString}` : ''}`;
        const response = await apiClient.get(url);
        return response.data;
    },

    /**
     * Get recruiter scorecard
     * @param {Object} filters - { startDate, endDate }
     */
    async getRecruiterScorecard(filters = {}) {
        const params = new URLSearchParams();
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);

        const queryString = params.toString();
        const url = `/recruiting/recruiters/scorecard${queryString ? `?${queryString}` : ''}`;
        const response = await apiClient.get(url);
        return response.data;
    },

    /**
     * Get filter options
     */
    async getFilterOptions() {
        const response = await apiClient.get('/recruiting/filters');
        return response.data;
    },

    // ===================================================================
    // HIRES DASHBOARD
    // ===================================================================

    /**
     * Get hires dashboard summary
     * @param {Object} filters - { startDate, endDate, department, recruiter, source }
     */
    async getHiresSummary(filters = {}) {
        const params = new URLSearchParams();
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        if (filters.department) params.append('department', filters.department);
        if (filters.recruiter) params.append('recruiter', filters.recruiter);
        if (filters.source) params.append('source', filters.source);

        const queryString = params.toString();
        const url = `/recruiting/hires/summary${queryString ? `?${queryString}` : ''}`;
        const response = await apiClient.get(url);
        return response.data;
    },

    /**
     * Get hires by month trend
     * @param {Object} filters - { months, department, recruiter }
     */
    async getHiresByMonth(filters = {}) {
        const params = new URLSearchParams();
        if (filters.months) params.append('months', filters.months);
        if (filters.department) params.append('department', filters.department);
        if (filters.recruiter) params.append('recruiter', filters.recruiter);

        const queryString = params.toString();
        const url = `/recruiting/hires/by-month${queryString ? `?${queryString}` : ''}`;
        const response = await apiClient.get(url);
        return response.data;
    },

    /**
     * Get hires by job title
     * @param {Object} filters - { startDate, endDate }
     */
    async getHiresByJobTitle(filters = {}) {
        const params = new URLSearchParams();
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);

        const queryString = params.toString();
        const url = `/recruiting/hires/by-job-title${queryString ? `?${queryString}` : ''}`;
        const response = await apiClient.get(url);
        return response.data;
    },

    /**
     * Get hires by source
     * @param {Object} filters - { startDate, endDate }
     */
    async getHiresBySource(filters = {}) {
        const params = new URLSearchParams();
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);

        const queryString = params.toString();
        const url = `/recruiting/hires/by-source${queryString ? `?${queryString}` : ''}`;
        const response = await apiClient.get(url);
        return response.data;
    },

    /**
     * Get hires by recruiter
     * @param {Object} filters - { startDate, endDate }
     */
    async getHiresByRecruiter(filters = {}) {
        const params = new URLSearchParams();
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);

        const queryString = params.toString();
        const url = `/recruiting/hires/by-recruiter${queryString ? `?${queryString}` : ''}`;
        const response = await apiClient.get(url);
        return response.data;
    },

    /**
     * Get recent hires list
     * @param {Object} filters - { limit, startDate, endDate }
     */
    async getRecentHires(filters = {}) {
        const params = new URLSearchParams();
        if (filters.limit) params.append('limit', filters.limit);
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);

        const queryString = params.toString();
        const url = `/recruiting/hires/recent${queryString ? `?${queryString}` : ''}`;
        const response = await apiClient.get(url);
        return response.data;
    }
};

export default recruitingService;
